import { Component, OnInit, Renderer2, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { AuthenticationService } from '../../../core/services/auth.service';
import { OktaUser } from '../../../core/models/user.models';
import { NotificationItem } from '../../shared/models/notification.model';
import { MenuService } from '../../shared/services/menu.service';
import { UserProfileService } from 'src/app/core/services/user.service';
import { NavigationEnd, Router } from '@angular/router';
import { TopbarItem } from '../../shared/models/topbar.model';
import { DEFAULT_TOPBAR_ITEM, TOPBAR_ITEMS } from '../../shared/config/topbar-meta';
import { AppState } from 'src/app/store';
import { Store } from '@ngrx/store';
import { getShowSearchBar } from 'src/app/store/search-bar/search-bar.selectors';
import { lastValueFrom, Subject, Subscription, takeUntil, filter, debounceTime, startWith } from 'rxjs';
import { setUserPreferences } from 'src/app/store/user-preferences/user-preference.actions';
import { UserPreferenceService } from 'src/app/core/services/user-preference.service';
import { getViewToggleConfig } from 'src/app/store/dashboard/dashboard.selectors';
import { ViewToggleConfig } from 'src/app/core/models/general.models';
import { setViewToggleKey } from 'src/app/store/dashboard/dashboard.actions';
import { DashboardService } from 'src/app/pages/dashboard/services/dashboard.service';
import { EventService } from 'src/app/core/services/event.service';
import { getKey } from 'src/app/core/utils/functions';
import { FilterService } from 'src/app/shared/services/filter.service';
@Component({
    selector: 'app-detached-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TopbarComponent implements OnInit, OnDestroy {
  notificationItems: Array<NotificationItem>;
  topbarItem: TopbarItem;

  openMobileMenu: boolean;
  isPhiHidden: boolean;
  isDarkTheme: boolean;
  loggedInUser: OktaUser;
  dropdownOpen: boolean = false;
  showSearchBarSub: Subscription;

  private destroy$= new Subject<void>();
  viewToggleConfig: ViewToggleConfig;
  viewToggleConfigSub: Subscription;

  constructor(
    private authService: AuthenticationService,
    public service: MenuService,
    private renderer: Renderer2,
    public userService: UserProfileService,
    private router: Router,
    private store: Store<AppState>,
    public userPreferenceService: UserPreferenceService,
    private dashboardService: DashboardService,
    private eventService: EventService,
    public filterService: FilterService,
  ) {
    this.router.events
      .pipe(
        startWith(new NavigationEnd(0, this.router.url, this.router.url)), // Emit the current URL on initialization
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        const dashboardKey = getKey(event.url);
        this.topbarItem = TOPBAR_ITEMS?.find(item => item.key === dashboardKey);
        if (!TOPBAR_ITEMS?.map(item => item?.key)?.includes(dashboardKey)) {
          this.topbarItem = { key: '', hasSearchBar: false, hasHidePhiButton: true, hasUserProfileSection: true, hasViewToggle: true };
        }
      });
  }

  ngOnInit() {
    // get the notifications
    this._fetchNotifications();
    this.setUserPreferenceSubscriptions();
    // logged in user
    this.loggedInUser = this.authService.currentUser();

    this.showSearchBarSub = this.store.select(getShowSearchBar).subscribe({
      next: data => {
        this.topbarItem = this.topbarItem || DEFAULT_TOPBAR_ITEM;
        this.topbarItem.hasSearchBar = this.topbarItem?.hasSearchBar && data;
      }
    });

    this.viewToggleConfigSub = this.eventService.subscribe('set-view-toggle-config', async () => {
      await this.dashboardService.setViewToggleConfig();
    });

    this.store.select(getViewToggleConfig)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.viewToggleConfig = data;
      });
  }
  ngOnDestroy(): void {
    this.showSearchBarSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.viewToggleConfigSub?.unsubscribe();
  }


  /**
   * Fetches the notification
   * Note: For now returns the hard coded notifications
   */
  _fetchNotifications() {
    
  }

  /**
   * Logout the use
   */
  logout() {
    this.authService.logout();
  }
  lockSidebar() {
    this.service.lockLeftMenuSidebar = !this.service.lockLeftMenuSidebar;
  }
  togglePHI() {
    try{
      this.updatePreferences();
    }
    catch(e){
      console.error(e);
    }
  }

  async toggleTheme() {
    try{
      this.isDarkTheme = !this.isDarkTheme;
      this.updatePreferences();
    }
    catch(e){
      console.error(e);
    }
  }

  async updatePreferences() {
    const userPreferencePayload = {
      theme: this.isDarkTheme ? 'dark' : 'light',
      hidePHI: this.isPhiHidden,
      active: true
    }
    this.store.dispatch(setUserPreferences({data: {
      theme: userPreferencePayload?.theme,
      hidePHI: userPreferencePayload?.hidePHI
    }}));

    await lastValueFrom(!this.userService.USER_PROFILE?.preferences 
      ? this.userPreferenceService.createUserPreferences(userPreferencePayload)
      : this.userPreferenceService.updateUserPreferences(userPreferencePayload));
  }

  setUserPreferenceSubscriptions() {
    this.userPreferenceService.isDarkTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.isDarkTheme = data;
      });

    this.userPreferenceService.isPHIHidden$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.isPhiHidden = data;
        if (this.isPhiHidden) {
          this.renderer.addClass(document.body, "hide-phi");
        } else {
          this.renderer.removeClass(document.body, "hide-phi");
        }
      });
  }

  /**
   * It is used to set the view toggle key.
   * @param key It contains the key.
   */
  onViewChange(key: string) {
    this.store.dispatch(setViewToggleKey({ key }));
    this.eventService.broadcast('refresh-widget');
  }
}
